import React, { Suspense, createContext, useContext, useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "../../components/ui/sonner";
import "./AuditApp.css";

// API Base URL
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Audit Context for authentication
const AuditContext = createContext();

export const useAudit = () => {
  const context = useContext(AuditContext);
  if (!context) {
    throw new Error('useAudit must be used within AuditProvider');
  }
  return context;
};

// Audit Provider
const AuditProvider = ({ children }) => {
  const [portalUser, setPortalUser] = useState(() => {
    const saved = localStorage.getItem('auditPortalUser');
    return saved ? JSON.parse(saved) : null;
  });

  const login = (user) => {
    setPortalUser(user);
    localStorage.setItem('auditPortalUser', JSON.stringify(user));
  };

  const logout = () => {
    setPortalUser(null);
    localStorage.removeItem('auditPortalUser');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalAuth');
  };

  return (
    <AuditContext.Provider value={{ portalUser, login, logout, isAuthenticated: !!portalUser, API_URL }}>
      {children}
    </AuditContext.Provider>
  );
};

// Eagerly import common pages to eliminate lazy-load delay on navigation
import PortalDashboard from "./portal/PortalDashboard";
import PortalClients from "./portal/PortalClients";
import PortalSessions from "./portal/PortalSessions";

// Lazy-load less frequent pages
const PortalLogin = React.lazy(() => import("./portal/PortalLogin"));
const PortalLayout = React.lazy(() => import("./portal/PortalLayout"));
const PortalDevices = React.lazy(() => import("./portal/PortalDevices"));
const PortalReports = React.lazy(() => import("./portal/PortalReports"));
const PortalSyncLogs = React.lazy(() => import("./portal/PortalSyncLogs"));
const PortalUsers = React.lazy(() => import("./portal/PortalUsers"));
const PortalConflicts = React.lazy(() => import("./portal/PortalConflicts"));
const PortalLeads = React.lazy(() => import("./portal/PortalLeads"));

// Page Loader
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
  </div>
);

// Portal Login Wrapper
const PortalLoginWrapper = () => {
  const { isAuthenticated } = useAudit();
  
  if (isAuthenticated) {
    return <Navigate to="/audit/dashboard" replace />;
  }
  
  return <PortalLogin />;
};

// Protected Route for Portal
const PortalProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAudit();
  
  if (!isAuthenticated) {
    return <Navigate to="/audit" replace />;
  }
  
  return children;
};

// Main Audit Routes
function AuditRoutes() {
  return (
    <Routes>
      {/* Portal Login */}
      <Route
        index
        element={
          <Suspense fallback={<PageLoader />}>
            <PortalLoginWrapper />
          </Suspense>
        }
      />
      
      {/* Portal Dashboard */}
      <Route
        path="dashboard"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalDashboard />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />
      
      {/* Portal Clients */}
      <Route
        path="clients"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalClients />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />
      
      {/* Portal Sessions */}
      <Route
        path="sessions"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalSessions />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />
      
      {/* Portal Devices */}
      <Route
        path="devices"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalDevices />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />
      
      {/* Portal Reports */}
      <Route
        path="reports"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalReports />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />
      
      {/* Portal Sync Logs */}
      <Route
        path="sync-logs"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalSyncLogs />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />
      
      {/* Portal Users */}
      <Route
        path="users"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalUsers />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />
      
      {/* Portal Conflicts */}
      <Route
        path="conflicts"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalConflicts />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />
      
      {/* Portal Website Enquiries */}
      <Route
        path="leads"
        element={
          <PortalProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PortalLayout>
                <PortalLeads />
              </PortalLayout>
            </Suspense>
          </PortalProtectedRoute>
        }
      />

      {/* Redirect any unknown routes to login */}
      <Route path="*" element={<Navigate to="/audit" replace />} />
    </Routes>
  );
}

// Main Audit App Component
export default function AuditApp() {
  return (
    <AuditProvider>
      <div className="audit-app min-h-screen bg-gray-50">
        <AuditRoutes />
        <Toaster position="top-right" richColors />
      </div>
    </AuditProvider>
  );
}

// Export context hook for use in child components
export { AuditContext, AuditProvider };
